import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useAuth } from "./AuthContext";
import { SCHEMAS } from "./schemas";
import { Switch } from "../components/ui/switch";

const get = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const next = { ...obj };
  let cursor = next;
  keys.slice(0, -1).forEach((k) => {
    cursor[k] = { ...(cursor[k] || {}) };
    cursor = cursor[k];
  });
  cursor[keys[keys.length - 1]] = value;
  return next;
};

const Field = ({ field, value, onChange }) => {
  const common = "field";
  if (field.type === "textarea")
    return <textarea rows={4} className={common} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;
  if (field.type === "richtext")
    return (
      <>
        <textarea rows={10} className={`${common} font-mono text-xs`} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />
        <p className="mt-2 text-[0.65rem] text-zinc-600">Use &lt;p&gt;, &lt;h2&gt;, &lt;a&gt; and &lt;strong&gt; for formatting.</p>
      </>
    );
  if (field.type === "select")
    return (
      <select className={common} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`}>
        {field.options.map((o) => {
          const [val, label] = Array.isArray(o) ? o : [o, o];
          return (
            <option key={val} value={val}>{label}</option>
          );
        })}
      </select>
    );
  if (field.type === "number")
    return (
      <input
        type="number"
        className={common}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        data-testid={`field-${field.key}`}
      />
    );
  if (field.type === "switch")
    return (
      <div className="pt-1">
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${field.key}`} />
      </div>
    );
  if (field.type === "timeline") {
    const list = value || [];
    return (
      <div className="space-y-3" data-testid={`field-${field.key}`}>
        {list.map((row, i) => (
          <div key={i} className="flex gap-2">
            <input className="field w-24" placeholder="Year" value={row.year || ""} onChange={(e) => onChange(list.map((r, j) => (j === i ? { ...r, year: e.target.value } : r)))} />
            <input className="field flex-1" placeholder="Milestone" value={row.text || ""} onChange={(e) => onChange(list.map((r, j) => (j === i ? { ...r, text: e.target.value } : r)))} />
            <button type="button" aria-label="Remove entry" onClick={() => onChange(list.filter((_, j) => j !== i))} className="border border-zinc-800 px-3 text-zinc-500 hover:text-kenya-red">
              <X size={14} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...list, { year: "", text: "" }])} className="btn-ghost !px-5 !py-2.5" data-testid="add-timeline-entry">
          <Plus size={13} /> Add entry
        </button>
      </div>
    );
  }
  return <input className={common} placeholder={field.placeholder} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;
};

export default function ResourceAdmin() {
  const { resource } = useParams();
  const schema = SCHEMAS[resource];
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!SCHEMAS[resource]) return setLoading(false);
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${resource}`);
      setItems(data);
    } catch (err) {
      toast.error(errText(err));
    }
    setLoading(false);
  }, [resource]);

  useEffect(() => {
    setEditing(null);
    load();
  }, [load]);

  if (!schema)
    return (
      <div data-testid="admin-unknown-section">
        <h1 className="display-3">Nothing here</h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
          There is no admin section called <span className="mono text-ochre">{resource}</span>. Pick one from the
          menu on the left.
        </p>
      </div>
    );

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/${resource}/${editing.id}`, editing);
      else await api.post(`/admin/${resource}`, editing);
      toast.success(`${schema.label} saved`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name || item.title}"?`)) return;
    try {
      await api.delete(`/admin/${resource}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div data-testid={`admin-${resource}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Content</p>
          <h1 className="display-2 mt-4">{resource}</h1>
        </div>
        <button onClick={() => setEditing({ ...schema.empty })} className="btn-primary" data-testid={`add-${resource}`}>
          <Plus size={14} /> New {schema.label.toLowerCase()}
        </button>
      </div>

      {editing ? (
        <form onSubmit={save} className="mt-10 border border-zinc-900 bg-ink-surface p-6 md:p-8" data-testid={`${resource}-form`}>
          <div className="flex items-center justify-between">
            <h2 className="display-3">{editing.id ? `Edit ${schema.label.toLowerCase()}` : `New ${schema.label.toLowerCase()}`}</h2>
            <button type="button" onClick={() => setEditing(null)} aria-label="Close editor" className="text-zinc-500 hover:text-white" data-testid={`${resource}-form-close`}>
              <X size={18} />
            </button>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {schema.fields.map((f) => (
              <div key={f.key} className={f.span === 2 ? "md:col-span-2" : ""}>
                <label className="overline">{f.label}</label>
                <div className="mt-2">
                  <Field field={f} value={get(editing, f.key)} onChange={(v) => setEditing((prev) => setPath(prev, f.key, v))} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid={`${resource}-save`}>
              {saving ? <Loader2 className="animate-spin" size={14} /> : null} Save
            </button>
            <button type="button" onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      ) : null}

      <div className="mt-10 overflow-x-auto border border-zinc-900">
        <table className="w-full min-w-[560px] text-sm" data-testid={`${resource}-table`}>
          <thead className="bg-ink-surface">
            <tr>
              {schema.columns.map(([key, label]) => (
                <th key={key} className="px-4 py-3 text-left overline">{label}</th>
              ))}
              <th className="px-4 py-3 text-right overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={schema.columns.length + 1} className="px-4 py-8 text-center text-zinc-600">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={schema.columns.length + 1} className="px-4 py-8 text-center text-zinc-600">Nothing here yet.</td></tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-zinc-900 transition-colors hover:bg-ink-hover" data-testid={`${resource}-row-${item.slug}`}>
                  {schema.columns.map(([key]) => (
                    <td key={key} className="px-4 py-2.5 text-zinc-300">
                      {typeof item[key] === "boolean" ? (item[key] ? "Yes" : "No") : String(item[key] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditing({ ...schema.empty, ...item })} aria-label="Edit" data-testid={`edit-${item.slug}`} className="border border-zinc-800 p-2 text-zinc-400 hover:border-maasai hover:text-ochre">
                        <Pencil size={13} />
                      </button>
                      {user?.role === "super_admin" && (
                        <button onClick={() => remove(item)} aria-label="Delete" data-testid={`delete-${item.slug}`} className="border border-zinc-800 p-2 text-zinc-400 hover:border-kenya-red hover:text-kenya-red">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {user?.role !== "super_admin" && (
        <p className="mt-4 text-xs text-zinc-600" data-testid="editor-permission-note">
          Editors can create and update content. Deleting requires a super admin.
        </p>
      )}
    </div>
  );
}
